import { Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox } from "@/components/ui/Checkbox";
import { Button } from "@/components/ui/Button";
import { InputGroup } from "@/components/ui/Input";
import { TextareaGroup } from "@/components/ui/Textarea";
import { toast } from "@/components/ui/Toast";
import { PageSkeleton } from "@/components/common/LoadingSkeleton";
import { api } from "@/lib/api-client";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// CreateRole — Screen 16 (CBzC0)
//
// Role creation with permissions matrix.
// Design: §8.5 Screen 16
// ─────────────────────────────────────────────

// ── Permissions matrix definition ────────────

// Dashboard is intentionally omitted: no backend route enforces a "Dashboard"
// subject via checkAbility. Access to the dashboard is gated on read Assessment
// / read AuditLog at the route level.
const RESOURCES = [
  "App Inventory",
  "Assessments",
  "Schemas",
  "Users",
  "Risks",
  "Reports",
  "Vendors",
] as const;

type Resource = (typeof RESOURCES)[number];

const ACTIONS = [
  "read",
  "create",
  "update",
  "delete",
  "fill",
  "review",
  "approve",
] as const;

type Action = (typeof ACTIONS)[number];

// Allowlist: only (resource, action) pairs that correspond to a real
// checkAbility() call in the backend. Derived from grepping
// packages/api/src/**/*.routes.ts — see docs/investigation/ITEM-006-findings.md.
const RESOURCE_ACTIONS: Record<Resource, readonly Action[]> = {
  "App Inventory": ["read", "create", "update", "delete"],
  Assessments:     ["read", "create", "update", "delete", "fill", "review", "approve"],
  Schemas:         ["read", "create", "update", "delete", "approve"],
  Users:           ["read", "create", "update", "delete"],
  // Risks are computed scores — only read is enforced (subject: Score)
  Risks:           ["read"],
  // Reports are server-side generated views — only read is enforced
  Reports:         ["read"],
  Vendors:         ["read", "create", "update", "delete"],
};

// Display labels for each resource — keyed by the raw Resource identifier,
// which also doubles as an object key throughout this file (RESOURCE_ACTIONS,
// RESOURCE_TO_SUBJECT, permissions matrix) and therefore cannot itself be
// translated. Only the rendered label changes; the underlying key stays put.
function buildResourceLabels(t: (key: string) => string): Record<Resource, string> {
  return {
    "App Inventory": t("nav.apps"),
    Assessments: t("nav.assessments"),
    Schemas: t("nav.schemas"),
    Users: t("nav.users"),
    Risks: t("nav.risks"),
    Reports: t("nav.reports"),
    Vendors: t("nav.vendors"),
  };
}

// Display labels for each action — keyed by the raw Action identifier, which
// is also the literal string sent to the API as `rules[].action`. Must stay
// untranslated in the data model; only the rendered label changes.
function buildActionLabels(t: (key: string) => string): Record<Action, string> {
  return {
    read: t("roles.actions.read"),
    create: t("common.create"),
    update: t("roles.actions.update"),
    delete: t("common.delete"),
    fill: t("roles.actions.fill"),
    review: t("roles.actions.review"),
    approve: t("common.approve"),
  };
}

type PermMatrix = Record<Resource, Record<Action, boolean>>;

function emptyMatrix(): PermMatrix {
  const m = {} as PermMatrix;
  for (const resource of RESOURCES) {
    m[resource] = {} as Record<Action, boolean>;
    for (const action of ACTIONS) {
      // Only seed allowed cells; the rest stay false and will be hidden in the UI
      m[resource][action] = false;
    }
  }
  return m;
}

// Map from display resource name to the PascalCase singular subject string
// that the backend uses in checkAbility() calls. CASL matching is case-sensitive.
const RESOURCE_TO_SUBJECT: Record<Resource, string> = {
  "App Inventory": "Entity",
  Assessments:     "Assessment",
  Schemas:         "Schema",
  Users:           "User",
  Risks:           "Score",
  Reports:         "Report",
  Vendors:         "Vendor",
};

// Reverse map (subject → display resource) for prefilling the matrix in edit mode.
const SUBJECT_TO_RESOURCE: Record<string, Resource> = Object.fromEntries(
  (Object.entries(RESOURCE_TO_SUBJECT) as [Resource, string][]).map(
    ([resource, subject]) => [subject, resource]
  )
) as Record<string, Resource>;

// ── Fetched-role shapes (GET /roles/:id) ─────
interface FetchedPolicy {
  id: string;
  rules?: Array<{ action: string; subject: string }>;
}
interface FetchedRole {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  policies?: FetchedPolicy[];
}

// ── Main component ────────────────────────────

export function CreateRole() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isEditMode = Boolean(id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<PermMatrix>(emptyMatrix());
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  // Policy ids that already exist on the role — replaced on save (edit mode).
  const [existingPolicyIds, setExistingPolicyIds] = useState<string[]>([]);

  // Edit mode: fetch the role and prefill name, description, and the matrix.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: FetchedRole }>(`/roles/${id}`)
      .then((res) => {
        if (cancelled) return;
        const role = res.data;
        if (!role) return;
        // System roles are immutable on the backend — don't present an editor
        // that would only 403 on save.
        if (role.isSystem) {
          toast.info(t("roles.systemRoleTitle"), t("roles.systemRoleInfo"));
          navigate("/roles");
          return;
        }
        setName(role.name ?? "");
        setDescription(role.description ?? "");
        const matrix = emptyMatrix();
        const policyIds: string[] = [];
        for (const policy of role.policies ?? []) {
          policyIds.push(policy.id);
          for (const rule of policy.rules ?? []) {
            const resource = SUBJECT_TO_RESOURCE[rule.subject];
            if (
              resource &&
              (RESOURCE_ACTIONS[resource] as readonly string[]).includes(rule.action)
            ) {
              matrix[resource][rule.action as Action] = true;
            }
          }
        }
        setPermissions(matrix);
        setExistingPolicyIds(policyIds);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          t("roles.loadFailedTitle"),
          err instanceof Error ? err.message : "Unknown error"
        );
        navigate("/roles");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  function togglePermission(resource: Resource, action: Action) {
    setPermissions((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        [action]: !prev[resource][action],
      },
    }));
  }

  // Toggle all allowed actions in a row
  function toggleRow(resource: Resource) {
    const allowed = RESOURCE_ACTIONS[resource];
    const allOn = allowed.every((a) => permissions[resource][a]);
    setPermissions((prev) => ({
      ...prev,
      [resource]: {
        ...prev[resource],
        ...Object.fromEntries(allowed.map((a) => [a, !allOn])),
      } as Record<Action, boolean>,
    }));
  }

  // Toggle all resources for an action (column) — only for resources where the
  // action is in their allowlist
  function toggleColumn(action: Action) {
    const eligible = RESOURCES.filter((r) => RESOURCE_ACTIONS[r].includes(action));
    const allOn = eligible.every((r) => permissions[r][action]);
    setPermissions((prev) => {
      const next = { ...prev };
      for (const r of eligible) {
        next[r] = { ...next[r], [action]: !allOn };
      }
      return next;
    });
  }

  function validate(): boolean {
    const next: typeof errors = {};
    if (!name.trim()) next.name = t("roles.nameRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    // Build rules array — only emit allowed (action, subject) pairs
    const rules: Array<{ action: string; subject: string }> = [];
    for (const resource of RESOURCES) {
      for (const action of RESOURCE_ACTIONS[resource]) {
        if (permissions[resource][action]) {
          rules.push({
            action,
            subject: RESOURCE_TO_SUBJECT[resource],
          });
        }
      }
    }

    // ── Edit mode: update the existing role and replace its policies ──────────
    if (isEditMode && id) {
      try {
        await api.put(`/roles/${id}`, {
          name: name.trim(),
          // Send empty string (not undefined) so clearing the field persists.
          description: description.trim(),
        });

        // Replace policies: delete the old ones first (fail closed — never leave
        // a permission the admin removed), then create one consolidated policy.
        for (const pId of existingPolicyIds) {
          try {
            await api.delete(`/roles/${id}/policies/${pId}`);
          } catch {
            // best-effort — a stale/already-removed policy 404s; ignore
          }
        }
        if (rules.length > 0) {
          await api.post(`/roles/${id}/policies`, { rules });
        }
        setExistingPolicyIds([]);

        toast.success(t("roles.roleUpdated"), `"${name}" ${t("roles.updatedSuffix")}`);
        navigate("/roles");
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        if (message.includes("409") || message.toLowerCase().includes("duplicate")) {
          setErrors({ name: t("roles.nameInUse") });
        } else {
          toast.error(t("roles.updateFailedTitle"), message);
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    let createdRoleId: string | null = null;
    try {
      const roleRes = await api.post<{ data: { id: string } }>("/roles", {
        name: name.trim(),
        description: description.trim() || undefined,
      });

      createdRoleId = roleRes.data?.id ?? null;

      if (rules.length > 0 && createdRoleId) {
        await api.post(`/roles/${createdRoleId}/policies`, { rules });
      }

      toast.success(t("roles.roleCreated"), `"${name}" ${t("roles.readyToAssignSuffix")}`);
      navigate("/roles");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      // If role was created but policy step failed, clean up the orphan role
      if (createdRoleId) {
        try {
          await api.delete(`/roles/${createdRoleId}`);
        } catch {
          // best-effort rollback — ignore cleanup errors
        }
      }
      if (message.includes("409") || message.toLowerCase().includes("duplicate")) {
        setErrors({ name: t("roles.nameInUse") });
      } else {
        toast.error(t("roles.createFailedTitle"), message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // Only count cells in the allowlist — max is 23 (sum of RESOURCE_ACTIONS lengths)
  const selectedCount = RESOURCES.reduce(
    (acc, r) => acc + RESOURCE_ACTIONS[r].filter((a) => permissions[r][a]).length,
    0
  );

  const resourceLabels = buildResourceLabels(t);
  const actionLabels = buildActionLabels(t);

  // Edit mode: wait for the existing role to load before showing the form.
  if (isLoading) return <PageSkeleton />;

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-caption text-text-muted">
        <button
          type="button"
          className="transition-colors hover:text-text-secondary"
          onClick={() => navigate("/roles")}
        >
          {t("roles.title")}
        </button>
        <span>/</span>
        <span className="text-text-secondary">
          {isEditMode ? t("roles.editRole") : t("roles.createNewRole")}
        </span>
      </nav>

      {/* Page header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-[24px] font-semibold text-text-primary">
          {isEditMode ? t("roles.editRole") : t("roles.createNewRole")}
        </h1>
        <p className="text-body text-text-secondary">
          {isEditMode
            ? t("roles.editSubtitle")
            : t("roles.createSubtitle")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Basic info card */}
        <div className="rounded-card border border-border-default bg-card p-6">
          <div className="mb-5 flex items-center gap-3 border-b border-border-subtle pb-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-card bg-hover">
              <Shield className="h-5 w-5 text-text-muted" />
            </div>
            <h2 className="text-[16px] font-medium text-text-primary">
              {t("roles.roleDetails")}
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            <InputGroup
              label={t("roles.name")}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("roles.namePlaceholder")}
              errorText={errors.name}
            />
            <TextareaGroup
              label={t("roles.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("roles.descriptionPlaceholder")}
              rows={3}
            />
          </div>
        </div>

        {/* Permissions matrix card */}
        <div className="rounded-card border border-border-default bg-card p-6">
          <div className="mb-5 flex items-center justify-between border-b border-border-subtle pb-5">
            <h2 className="text-[16px] font-medium text-text-primary">
              {t("roles.permissionsMatrix")}
            </h2>
            <span className="text-caption text-text-muted">
              {t("roles.permissionsSelected", { count: selectedCount })}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="w-40 px-4 py-3 text-left text-[12px] font-medium uppercase tracking-[1px] text-text-muted">
                    {t("roles.resource")}
                  </th>
                  {ACTIONS.map((action) => (
                    <th
                      key={action}
                      className="min-w-[80px] px-3 py-3 text-center text-[12px] font-medium uppercase tracking-[1px] text-text-muted"
                    >
                      <button
                        type="button"
                        className="flex w-full flex-col items-center gap-1 transition-colors hover:text-text-secondary"
                        onClick={() => toggleColumn(action)}
                        title={`${t("roles.toggleAllPrefix")} ${actionLabels[action]}`}
                      >
                        {actionLabels[action]}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RESOURCES.map((resource) => (
                  <tr
                    key={resource}
                    className="border-b border-border-subtle last:border-0"
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="text-left text-[14px] font-medium text-text-secondary transition-colors hover:text-text-primary"
                        onClick={() => toggleRow(resource)}
                        title={`${t("roles.toggleAllForPrefix")} ${resourceLabels[resource]}`}
                      >
                        {resourceLabels[resource]}
                      </button>
                    </td>
                    {ACTIONS.map((action) => {
                      const allowed = RESOURCE_ACTIONS[resource].includes(action);
                      return (
                        <td
                          key={action}
                          className="px-3 py-3 text-center"
                        >
                          {allowed ? (
                            <Checkbox
                              checked={permissions[resource][action]}
                              onCheckedChange={() => togglePermission(resource, action)}
                              aria-label={`${resourceLabels[resource]} ${actionLabels[action]}`}
                            />
                          ) : (
                            <span className="text-[14px] text-text-muted select-none" aria-hidden="true">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-caption text-text-muted">
            {t("roles.toggleHint")}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/roles")}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            leadingIcon={<Shield className="h-4 w-4" />}
            isLoading={isSubmitting}
          >
            {isEditMode ? t("common.save") : t("roles.newRole")}
          </Button>
        </div>
      </form>
    </>
  );
}
